import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { storiesAPI } from '../services/api';
import { useProfile } from '../context/ProfileContext';

export default function StoryBuilderScreen() {
  const navigate = useNavigate();
  const { storyId } = useParams();
  const { profile, refreshProfile } = useProfile();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(null);

  const WRITING_PROMPTS = [
    'Once upon a time in a magical forest...',
    'The little robot woke up and discovered...',
    'Deep under the ocean, there was a secret city...',
    'A dragon and a princess became best friends when...',
    'The spaceship landed on a planet made of candy...'
  ];

  useEffect(() => {
    if (storyId && profile) {
      storiesAPI.getOne(profile.id, storyId)
        .then(story => {
          setTitle(story.title);
          setContent(story.content || '');
        })
        .catch(console.error);
    }
  }, [storyId, profile]);

  const handleSave = async () => {
    if (!profile || !title.trim()) return;
    setSaving(true);
    try {
      if (storyId) {
        await storiesAPI.update(profile.id, storyId, { title, content });
      } else {
        const result = await storiesAPI.create({ profileId: profile.id, title, content });
        setPointsEarned(result.points);
        refreshProfile();
      }
      setSaved(true);
      setTimeout(() => { setSaved(false); setPointsEarned(null); }, 3000);
    } catch (e) {
      alert('Failed to save story. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const usePrompt = (prompt) => {
    setContent(prev => prev ? prev + '\n\n' + prompt : prompt);
  };

  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #FECA57, #FF9F43)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => navigate('/stories')} style={{ background: 'rgba(255,255,255,0.3)', color: 'var(--dark)' }}>←</button>
        <h1 style={{ fontFamily: 'var(--font-title)', fontSize: '1.4rem', flex: 1 }}>✍️ Story Builder</h1>
        <button
          className="btn btn-sm"
          onClick={handleSave}
          disabled={saving || !title.trim()}
          style={{ background: 'rgba(255,255,255,0.9)', color: '#FF9F43', fontWeight: 800 }}
        >
          {saving ? '...' : saved ? '✅' : '💾 Save'}
        </button>
      </div>

      <div className="screen-body" style={{ paddingTop: 16 }}>
        {/* Title Input */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontWeight: 800, fontSize: '0.85rem', display: 'block', marginBottom: 6 }}>Story Title</label>
          <input
            className="input"
            placeholder="Give your story a name..."
            value={title}
            onChange={e => setTitle(e.target.value)}
            style={{ fontSize: '1.1rem', fontWeight: 700 }}
          />
        </div>

        {/* Writing Prompts */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 800, fontSize: '0.85rem', marginBottom: 8 }}>💡 Story Starters</div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {WRITING_PROMPTS.map((prompt, i) => (
              <button
                key={i}
                onClick={() => usePrompt(prompt)}
                style={{
                  background: 'var(--bg)', border: '2px solid var(--border)',
                  borderRadius: 12, padding: '8px 12px', cursor: 'pointer',
                  whiteSpace: 'nowrap', fontSize: '0.8rem', fontFamily: 'var(--font-body)',
                  fontWeight: 600, flexShrink: 0, maxWidth: 200, textOverflow: 'ellipsis',
                  overflow: 'hidden'
                }}
              >
                {prompt.substring(0, 35)}...
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ fontWeight: 800, fontSize: '0.85rem' }}>Your Story</label>
            <span style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>{wordCount} words</span>
          </div>
          <textarea
            className="input"
            placeholder="Start writing your amazing story here..."
            value={content}
            onChange={e => setContent(e.target.value)}
            style={{ minHeight: 300, lineHeight: 1.8, fontSize: '1rem' }}
          />
        </div>

        {/* Word count milestone */}
        {wordCount >= 10 && wordCount < 50 && (
          <div style={{ background: '#E8F5E9', border: '2px solid var(--green)', borderRadius: 12, padding: 12, marginTop: 12, fontSize: '0.85rem' }}>
            ✏️ Great start! Keep writing to earn your <strong>Story Star</strong>!
          </div>
        )}
        {wordCount >= 50 && (
          <div style={{ background: '#FFF9E6', border: '2px solid var(--yellow)', borderRadius: 12, padding: 12, marginTop: 12, fontSize: '0.85rem' }}>
            🌟 Amazing! You've written {wordCount} words! Save your story to earn <strong>+7 points</strong>!
          </div>
        )}

        {pointsEarned && (
          <div className="points-popup">⭐ +{pointsEarned} points! Story saved!</div>
        )}
      </div>
    </div>
  );
}
